import type { FC } from "react";

import {
  type AddressFormVariant,
  isAddressFormExtendedWithProvince,
  type KsaProvince,
  type LocationServiceAutocompleteService,
  type RegionWithRequiredFields,
} from "@jti/fe-common";
import type { IGetOrderDeliveryLimits, PostCodeConfig } from "@jti/magento";

import {
  ExtendedWithProvinceForm,
  JordanForm,
  SaudiArabiaForm,
  TaiwanAddressForm,
  UnitedArabEmiratesForm,
} from "../AddressForms";
import { AsianAddressForm } from "../AsianAddressForm";
import { SingleFieldLookupAddressForm } from "../SingleFieldLookupAddressForm";
import { StandardAddressForm } from "../StandardAddressForm";

export type AddressFormProviderProps = {
  fieldName?(name: string): string;
  postCodeConfig?: PostCodeConfig;
  locationServiceAutocompleteProvider?: LocationServiceAutocompleteService;
  orderDeliveryLimits?: IGetOrderDeliveryLimits;
  formType: AddressFormVariant;
  regions?: RegionWithRequiredFields[];
  isRegionFieldOptional?: boolean;
  isSingleFieldAddressLookup?: boolean;
  isStreetFieldCombined?: boolean;
  ksaProvinces?: KsaProvince[];
};

export const AddressFormProvider: FC<AddressFormProviderProps> = ({
  fieldName,
  postCodeConfig,
  locationServiceAutocompleteProvider = "google",
  formType = "standard",
  orderDeliveryLimits,
  regions,
  isRegionFieldOptional,
  isSingleFieldAddressLookup,
  isStreetFieldCombined,
  ksaProvinces = [],
}) => {
  if (formType === "single_field_lookup") {
    return (
      <SingleFieldLookupAddressForm fieldName={fieldName} autocompleteProvider={locationServiceAutocompleteProvider} />
    );
  }

  if (formType === "saudi_arabia") {
    return <SaudiArabiaForm fieldName={fieldName} ksaProvinces={ksaProvinces} postCodeConfig={postCodeConfig} />;
  }

  if (formType === "united_arab_emirates") {
    return <UnitedArabEmiratesForm fieldName={fieldName} />;
  }

  if (formType === "jordan") {
    return (
      <JordanForm fieldName={fieldName} locationServiceAutocompleteProvider={locationServiceAutocompleteProvider} />
    );
  }

  if (formType === "extended_asian" || formType === "extended_asian_reversed") {
    return (
      <AsianAddressForm
        fieldName={fieldName}
        postCodeConfig={postCodeConfig}
        enableStreetNameAutocomplete={true}
        orderDeliveryLimits={orderDeliveryLimits}
        locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
      />
    );
  }

  if (formType === "with_regions" || formType === "with_regions_reversed") {
    return (
      <StandardAddressForm
        fieldName={fieldName}
        postCodeConfig={postCodeConfig}
        enableStreetNameAutocomplete={true}
        orderDeliveryLimits={orderDeliveryLimits}
        locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
        showRegionField={true}
        regions={regions}
        isRegionFieldOptional={isRegionFieldOptional}
      />
    );
  }

  if (formType === "taiwan") {
    return <TaiwanAddressForm fieldName={fieldName} postCodeConfig={postCodeConfig} />;
  }

  if (isAddressFormExtendedWithProvince(formType)) {
    return (
      <ExtendedWithProvinceForm
        fieldName={fieldName}
        postCodeConfig={postCodeConfig}
        orderDeliveryLimits={orderDeliveryLimits}
        locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
        isStreetFieldCombined={isStreetFieldCombined}
        enableStreetNameAutocomplete={true}
      />
    );
  }

  return (
    <StandardAddressForm
      fieldName={fieldName}
      enableStreetNameAutocomplete={true}
      postCodeConfig={postCodeConfig}
      orderDeliveryLimits={orderDeliveryLimits}
      locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
      isSingleFieldAddressLookup={isSingleFieldAddressLookup}
    />
  );
};
